import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertCircle, Building2, CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@shared/routes";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

type Ambiente = "producao" | "sandbox" | "simulado";
type Agenda = "diario" | "semanal" | "mensal" | "manual";
interface Estado {
  ambiente: Ambiente;
  status: "nao_iniciada" | "aguardando_documentos" | "em_analise" | "aprovada" | "rejeitada" | "dados_expirando" | "dados_expirados";
  agendaRepasse: Agenda;
  prontaParaOperacoes: boolean;
  alerta: string | null;
  cadastro: {
    razaoSocial: string; cnpj: string | null; email: string; telefone: string | null;
    logradouro: string | null; numero: string | null; complemento: string | null;
    bairro: string | null; cidade: string; uf: string; cep: string | null;
    companyType: "MEI" | "LIMITED" | "INDIVIDUAL" | "ASSOCIATION" | null;
    taxRegime: "MEI" | "NATIONAL_SIMPLE" | "NORMAL_REGIME" | "UNKNOWN" | null;
    incomeValue: number | null; pendencias: string[];
  };
}

const STATUS: Record<Estado["status"], string> = {
  nao_iniciada: "Não iniciada", aguardando_documentos: "Aguardando documentos",
  em_analise: "Em análise", aprovada: "Aprovada", rejeitada: "Rejeitada",
  dados_expirando: "Dados a vencer", dados_expirados: "Dados expirados",
};
const NOME_AMBIENTE: Record<Ambiente, string> = { producao: "Produção", sandbox: "Sandbox", simulado: "Simulado" };

// Fora de produção a conta opera assim que tem account_id/wallet_id, sem aprovação do Asaas: o status gravado
// ("aguardando_documentos") não muda sozinho e contradiria o aviso "pronta". Em produção pronta já implica aprovada.
function rotuloStatus(e: Estado): string {
  return e.prontaParaOperacoes && e.status !== "aprovada" ? `Pronta (${NOME_AMBIENTE[e.ambiente]})` : STATUS[e.status];
}

const AGENDA: Record<Agenda, string> = { diario: "Diário", semanal: "Semanal", mensal: "Mensal", manual: "Manual" };

const vazio = {
  razaoSocial: "", cnpj: "", email: "", telefone: "", logradouro: "", numero: "",
  complemento: "", bairro: "", cidade: "", uf: "", cep: "", companyType: "LIMITED",
  taxRegime: "NATIONAL_SIMPLE", incomeValue: "",
};
type Form = typeof vazio;

function erroDaResposta(texto: string, fallback: string) {
  try {
    const json = JSON.parse(texto);
    return json.pendencias?.length ? `${json.message}: ${json.pendencias.join(", ")}` : json.message || fallback;
  } catch { return fallback; }
}

const AMBIENTES: Ambiente[] = ["producao", "sandbox", "simulado"];
const CHAVE_AMBIENTE = "laudolegal:asaas-ambiente";

// Lembra o ambiente escolhido entre recargas (F5). Conveniência por navegador: se o storage falhar ou o valor for
// desconhecido, vale o ambiente padrão da tela.
function ambienteLembrado(padrao: Ambiente): Ambiente {
  try {
    const salvo = localStorage.getItem(CHAVE_AMBIENTE);
    return AMBIENTES.includes(salvo as Ambiente) ? (salvo as Ambiente) : padrao;
  } catch { return padrao; }
}

export function ContaAsaasRepasses({ token, ambienteInicial = "producao" }: { token?: string; ambienteInicial?: Ambiente }) {
  const { toast } = useToast();
  const [ambiente, setAmbiente] = useState<Ambiente>(() => ambienteLembrado(ambienteInicial));
  const escolherAmbiente = (novo: Ambiente) => {
    setAmbiente(novo);
    try { localStorage.setItem(CHAVE_AMBIENTE, novo); } catch { /* sem storage: só não lembra */ }
  };
  const [agenda, setAgenda] = useState<Agenda>("diario");
  const [aceitouTermos, setAceitouTermos] = useState(false);
  const [form, setForm] = useState<Form>(vazio);
  const headers = { Authorization: `Bearer ${token ?? ""}` };
  const requisitar = async (method: string, path: string, body?: unknown) => {
    const resposta = await fetch(path, {
      method,
      headers: { ...headers, ...(body ? { "Content-Type": "application/json" } : {}) },
      body: body ? JSON.stringify(body) : undefined,
    });
    const texto = await resposta.text();
    if (!resposta.ok) throw new Error(erroDaResposta(texto, "Não foi possível concluir a operação."));
    return JSON.parse(texto) as Estado;
  };
  const estado = useQuery<Estado>({
    queryKey: [api.asaasSubconta.obter.path, ambiente],
    queryFn: () => requisitar("GET", `${api.asaasSubconta.obter.path}?ambiente=${ambiente}`),
    enabled: Boolean(token),
  });
  useEffect(() => {
    if (!estado.data) return;
    const c = estado.data.cadastro;
    setAgenda(estado.data.agendaRepasse);
    setForm({
      razaoSocial: c.razaoSocial ?? "", cnpj: c.cnpj ?? "", email: c.email ?? "", telefone: c.telefone ?? "",
      logradouro: c.logradouro ?? "", numero: c.numero ?? "", complemento: c.complemento ?? "",
      bairro: c.bairro ?? "", cidade: c.cidade ?? "", uf: c.uf ?? "", cep: c.cep ?? "",
      companyType: c.companyType ?? "LIMITED", taxRegime: c.taxRegime ?? "NATIONAL_SIMPLE",
      incomeValue: c.incomeValue == null ? "" : String(c.incomeValue),
    });
  }, [estado.data]);
  const atualizar = (novo: Estado, titulo: string) => {
    estado.refetch();
    setAgenda(novo.agendaRepasse);
    toast({ title: titulo });
  };
  const cadastro = useMutation({
    mutationFn: () => requisitar(api.asaasSubconta.cadastro.method, api.asaasSubconta.cadastro.path, {
      ...form, incomeValue: Number(form.incomeValue), complemento: form.complemento || null,
    }),
    onSuccess: (novo) => atualizar(novo, "Cadastro empresarial salvo"),
    onError: (e: Error) => toast({ title: e.message, variant: "destructive" }),
  });
  const criar = useMutation({
    mutationFn: () => requisitar(api.asaasSubconta.criar.method, api.asaasSubconta.criar.path, { ambiente, aceitouTermos, agendaRepasse: agenda }),
    onSuccess: (novo) => atualizar(novo, ambiente === "simulado" ? "Subconta simulada pronta" : "Cadastro enviado ao Asaas"),
    onError: (e: Error) => toast({ title: e.message, variant: "destructive" }),
  });
  const salvarAgenda = useMutation({
    mutationFn: () => requisitar(api.asaasSubconta.agendamento.method, api.asaasSubconta.agendamento.path, { ambiente, agendaRepasse: agenda }),
    onSuccess: (novo) => atualizar(novo, "Agenda de repasses atualizada"),
    onError: (e: Error) => toast({ title: e.message, variant: "destructive" }),
  });
  const set = (campo: keyof Form, valor: string) => setForm((atual) => ({ ...atual, [campo]: valor }));
  const pendencias = estado.data?.cadastro.pendencias ?? [];

  return (
    <Card className="border-none shadow-md" data-testid="conta-asaas-repasses">
      <CardHeader className="pb-4">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
          <div className="flex gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10"><Building2 className="h-5 w-5 text-primary" /></div>
            <div>
              <CardTitle className="text-base">Conta Asaas e Repasses</CardTitle>
              <CardDescription className="mt-0.5">Os repasses são acumulados por laudo finalizado e enviados conforme a agenda.</CardDescription>
            </div>
          </div>
          {estado.data && <Badge variant={estado.data.prontaParaOperacoes ? "default" : "secondary"} data-testid="subconta-status">{rotuloStatus(estado.data)}</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5"><Label>Ambiente</Label><Select value={ambiente} onValueChange={(v) => escolherAmbiente(v as Ambiente)}><SelectTrigger data-testid="subconta-ambiente"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="producao">Produção</SelectItem><SelectItem value="sandbox">Sandbox Asaas</SelectItem><SelectItem value="simulado">Simulado local</SelectItem></SelectContent></Select></div>
          <div className="space-y-1.5"><Label>Agenda de repasses</Label><div className="flex gap-2"><Select value={agenda} onValueChange={(v) => setAgenda(v as Agenda)}><SelectTrigger data-testid="subconta-agenda"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(AGENDA).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select><Button type="button" variant="outline" onClick={() => salvarAgenda.mutate()} disabled={!estado.data || estado.data.status === "nao_iniciada" || salvarAgenda.isPending}>Salvar</Button></div></div>
        </div>

        {estado.isLoading && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Carregando cadastro…</div>}
        {estado.isError && <Alert variant="destructive" data-testid="subconta-erro"><AlertCircle className="h-4 w-4" /><AlertTitle>Não foi possível carregar a conta</AlertTitle><AlertDescription><Button className="mt-2" size="sm" variant="outline" onClick={() => estado.refetch()}>Tentar novamente</Button></AlertDescription></Alert>}
        {estado.data?.alerta && <Alert data-testid="subconta-alerta"><AlertCircle className="h-4 w-4" /><AlertTitle>Atenção ao cadastro</AlertTitle><AlertDescription>{estado.data.alerta}</AlertDescription></Alert>}
        {estado.data?.prontaParaOperacoes && <Alert className="border-emerald-200 bg-emerald-50/60" data-testid="subconta-pronta"><CheckCircle2 className="h-4 w-4 text-emerald-600" /><AlertTitle>Conta pronta para operações</AlertTitle><AlertDescription>Os repasses desta Vistoriadora já podem ser transferidos via Asaas neste ambiente.</AlertDescription></Alert>}

        {estado.data && (
          <details className="rounded-lg border bg-muted/20 p-4" open={pendencias.length > 0}>
            <summary className="cursor-pointer font-medium">Dados empresariais {pendencias.length ? `· ${pendencias.length} pendência(s)` : "· revisados"}</summary>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {([
                ["razaoSocial", "Razão social"], ["cnpj", "CNPJ"], ["email", "E-mail"], ["telefone", "Telefone"],
                ["logradouro", "Logradouro"], ["numero", "Número"], ["complemento", "Complemento"], ["bairro", "Bairro"],
                ["cidade", "Cidade"], ["uf", "UF"], ["cep", "CEP"], ["incomeValue", "Faturamento/renda mensal (R$)"],
              ] as const).map(([campo, label]) => <div className="space-y-1.5" key={campo}><Label htmlFor={`asaas-${campo}`}>{label}</Label><Input id={`asaas-${campo}`} data-testid={`asaas-${campo}`} type={campo === "incomeValue" ? "number" : "text"} min={campo === "incomeValue" ? "0.01" : undefined} step={campo === "incomeValue" ? "0.01" : undefined} value={form[campo]} onChange={(e) => set(campo, e.target.value)} /></div>)}
              <div className="space-y-1.5"><Label>Tipo de empresa</Label><Select value={form.companyType} onValueChange={(v) => set("companyType", v)}><SelectTrigger data-testid="asaas-companyType"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="MEI">MEI</SelectItem><SelectItem value="LIMITED">Sociedade limitada</SelectItem><SelectItem value="INDIVIDUAL">Empresário individual</SelectItem><SelectItem value="ASSOCIATION">Associação</SelectItem></SelectContent></Select></div>
              <div className="space-y-1.5"><Label>Regime tributário</Label><Select value={form.taxRegime} onValueChange={(v) => set("taxRegime", v)}><SelectTrigger data-testid="asaas-taxRegime"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="MEI">MEI</SelectItem><SelectItem value="NATIONAL_SIMPLE">Simples Nacional</SelectItem><SelectItem value="NORMAL_REGIME">Regime normal</SelectItem><SelectItem value="UNKNOWN">Não informado</SelectItem></SelectContent></Select></div>
            </div>
            <Button type="button" className="mt-4" variant="outline" onClick={() => cadastro.mutate()} disabled={cadastro.isPending}>{cadastro.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salvar dados empresariais</Button>
          </details>
        )}

        {estado.data && !estado.data.prontaParaOperacoes && (
          <div className="space-y-3 rounded-lg border p-4">
            <p className="text-sm" data-testid="subconta-email-ativacao">O Asaas enviará o e-mail de ativação da conta para <strong className="break-all">{estado.data.cadastro.email}</strong>. Confira se o endereço está correto e se alguém o acessa: é por ele que a Vistoriadora ativa a conta e acessa o painel do Asaas.</p>
            <div className="flex items-start gap-2"><Checkbox id="asaas-termos" checked={aceitouTermos} onCheckedChange={(v) => setAceitouTermos(v === true)} /><Label htmlFor="asaas-termos" className="font-normal leading-5">Confirmo que os dados estão corretos, inclusive o e-mail de ativação, e autorizo a criação da subconta padrão Asaas.</Label></div>
            <Button type="button" data-testid="subconta-criar" onClick={() => criar.mutate()} disabled={!aceitouTermos || pendencias.length > 0 || criar.isPending}>{criar.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{estado.data.status === "nao_iniciada" ? "Criar conta Asaas" : "Reenviar cadastro"}</Button>
            {ambiente === "producao" && estado.data.status !== "aprovada" && <p className="text-sm text-muted-foreground" data-testid="subconta-bloqueio">Até a aprovação do Asaas, os repasses desta Vistoriadora são feitos fora da plataforma pelo Owner.</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
